import { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { site } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  nom: z.string().trim().min(2, "Votre nom est obligatoire."),
  email: z.string().trim().email("Veuillez saisir une adresse email valide."),
  sujet: z.string().trim().min(2, "Le sujet est obligatoire."),
  message: z.string().trim().min(10, "Votre message doit comporter au moins 10 caractères."),
  rgpd: z.literal(true, { errorMap: () => ({ message: "Vous devez accepter le traitement de vos données." }) }),
  website: z.string().max(0),
});

export const ContactForm = () => {
  const navigate = useNavigate();
  const startedAt = useRef(Date.now());
  const [sending, setSending] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { nom: "", email: "", sujet: "", message: "", rgpd: false, website: "" },
  });

  const onSubmit = async (values) => {
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...values, started_at: startedAt.current });
      toast.success("Votre message a bien été envoyé. Nous vous répondons sous 48 heures ouvrées.");
      navigate("/merci", { state: { kind: "contact" } });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(`${typeof detail === "string" ? detail : "L'envoi a échoué."} Vous pouvez nous joindre au ${site.phone} ou à ${site.email}.`, { duration: 9000 });
    } finally {
      setSending(false);
    }
  };

  const cls = (n) => `field ${errors[n] ? "field-error" : ""}`;
  const Err = ({ name }) => errors[name] ? <p role="alert" className="mt-1.5 text-xs font-medium text-red-700" data-testid={`contact-${name}-error`}>{errors[name].message}</p> : null;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5" data-testid="contact-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="c-nom" className="label">Nom <span className="text-gold" aria-hidden="true">*</span></label>
          <input id="c-nom" className={cls("nom")} autoComplete="name" {...register("nom")} aria-invalid={!!errors.nom} data-testid="contact-nom-input" />
          <Err name="nom" />
        </div>
        <div>
          <label htmlFor="c-email" className="label">Email <span className="text-gold" aria-hidden="true">*</span></label>
          <input id="c-email" type="email" className={cls("email")} autoComplete="email" {...register("email")} aria-invalid={!!errors.email} data-testid="contact-email-input" />
          <Err name="email" />
        </div>
      </div>
      <div>
        <label htmlFor="c-sujet" className="label">Sujet <span className="text-gold" aria-hidden="true">*</span></label>
        <input id="c-sujet" className={cls("sujet")} {...register("sujet")} aria-invalid={!!errors.sujet} data-testid="contact-sujet-input" />
        <Err name="sujet" />
      </div>
      <div>
        <label htmlFor="c-message" className="label">Message <span className="text-gold" aria-hidden="true">*</span></label>
        <textarea id="c-message" rows={5} className={cls("message")} {...register("message")} aria-invalid={!!errors.message} data-testid="contact-message-input" />
        <Err name="message" />
      </div>
      <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
        <label htmlFor="c-website">Ne pas remplir</label>
        <input id="c-website" tabIndex={-1} autoComplete="off" {...register("website")} />
      </div>
      <label className="flex items-start gap-3 text-sm text-ink">
        <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-gold" {...register("rgpd")} aria-invalid={!!errors.rgpd} data-testid="contact-rgpd-checkbox" />
        <span>
          J'accepte que mes données soient traitées par {site.name} pour répondre à ma demande, conformément à la{" "}
          <Link to="/politique-de-confidentialite" className="text-slate underline decoration-gold/60 underline-offset-4 hover:text-gold">politique de confidentialité</Link>. <span className="text-gold" aria-hidden="true">*</span>
        </span>
      </label>
      <Err name="rgpd" />
      <button type="submit" disabled={sending} className="btn-night !px-8 !py-4 text-base disabled:opacity-60" data-testid="contact-submit-btn">
        {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Envoyer le message
      </button>
    </form>
  );
};
